// Payments & Billing — read-only platform plan, loyalty progress, invoices.
//
// Spec: /root/TKOS_MASTER_CONTEXT.md §Payments & Billing + commission tiers
// under "THE BUSINESS MODEL", and inventory_sections/01_retailer.md §Payments.
// Source-of-truth render lives in portal-standalone.html ~29073-29196
// (`renderPaymentsPage`, internal TIERS array). Note: the source-of-truth has
// DUPLICATE `<div id="page-payments">` blocks at lines 2924 and 2941 — a known
// Manus copy-paste bug. We rebuild cleanly with a single section.
//
// Backend wiring
// --------------
// kitchenos-api/modules/stripe.js exposes the retailer-facing endpoints we
// need (mounted at /api/stripe by server.js:2003-2004):
//   GET /api/stripe/connect/status      — connected, tier, progress, gmv
//   GET /api/stripe/commission-summary  — mtd / ytd commission, gmv, tier
//   GET /api/stripe/transactions        — paginated transaction history
//   GET /api/stripe/config               — public tiers + cap
// There is NO retailer "platform invoices" or "outstanding balances" endpoint
// at this time. Recent invoices are derived from /commission-summary if
// available, otherwise rendered as demo data with a "Demo data" badge. The
// Outstanding Balances section is stubbed with realistic figures for now.
//
// Tier model
// ----------
// The 5 tiers and their rates come from the kitchenos-api source-of-truth
// (modules/stripe.js:39-45) — these are the AUTHORITATIVE rates the backend
// actually uses to charge commission. They match the marketing site
// (/var/www/kitchenos/pricing.html: 3.3 / 2.8 / 2.3 / 1.8 / 1.5%) but the
// master doc only lists Tier 1 = 3.3% explicitly.
//
//   Tier 1: 3.3%  (£0      – £250k    rolling 12-month platform turnover)
//   Tier 2: 2.8%  (£250k   – £500k)
//   Tier 3: 2.3%  (£500k   – £750k)
//   Tier 4: 1.8%  (£750k   – £1m)
//   Tier 5: 1.5%  (£1m+ — absolute floor)

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import { registerPage } from "../../shell/router";
import { demoDataAllowed } from "../../shell/onboarding";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Tier model (mirror of kitchenos-api/modules/stripe.js TIERS) ─────────────

interface Tier {
  n: 1 | 2 | 3 | 4 | 5;
  /** Lower bound (inclusive) of rolling 12-month platform turnover, GBP. */
  minGbp: number;
  /** Upper bound (exclusive). `null` = open-ended top tier. */
  maxGbp: number | null;
  /** Commission rate %. */
  rate: number;
  label: string;
}

const TIERS: readonly Tier[] = [
  { n: 1, minGbp: 0,        maxGbp: 250_000,    rate: 3.3, label: "Tier 1" },
  { n: 2, minGbp: 250_000,  maxGbp: 500_000,    rate: 2.8, label: "Tier 2" },
  { n: 3, minGbp: 500_000,  maxGbp: 750_000,    rate: 2.3, label: "Tier 3" },
  { n: 4, minGbp: 750_000,  maxGbp: 1_000_000,  rate: 1.8, label: "Tier 4" },
  { n: 5, minGbp: 1_000_000, maxGbp: null,      rate: 1.5, label: "Tier 5" },
];

// Enterprise ladder — mirror of kitchenos-api/modules/stripe.js ENTERPRISE_TIERS.
// Assigned manually per retailer by an admin; same £500 cap + rolling-12-month
// logic as standard, 1.5% floor (top band open-ended above £1m).
const ENTERPRISE_TIERS: readonly Tier[] = [
  { n: 1, minGbp: 0,        maxGbp: 250_000,    rate: 2.5,  label: "Tier 1" },
  { n: 2, minGbp: 250_000,  maxGbp: 500_000,    rate: 2.25, label: "Tier 2" },
  { n: 3, minGbp: 500_000,  maxGbp: 750_000,    rate: 2.0,  label: "Tier 3" },
  { n: 4, minGbp: 750_000,  maxGbp: 1_000_000,  rate: 1.75, label: "Tier 4" },
  { n: 5, minGbp: 1_000_000, maxGbp: null,      rate: 1.5,  label: "Tier 5" },
];

type Plan = "standard" | "enterprise";

function tiersForPlan(plan: Plan): readonly Tier[] {
  return plan === "enterprise" ? ENTERPRISE_TIERS : TIERS;
}

// ── Server response shapes (kitchenos-api/modules/stripe.js) ─────────────────

interface ServerTier {
  tier: number;
  name: string;
  ratePct: string;
  rateBps: number;
  label: string;
}

interface ServerProgress {
  progressFraction: number;
  distanceGbp?: string;
  distancePence?: number;
  message: string;
  nextTierName: string | null;
  nextRatePct: string | null;
}

interface ConnectStatus {
  connected: boolean;
  annualGmvPence: number;
  annualGmvGbp: string;
  plan?: Plan;
  tier: ServerTier;
  progress: ServerProgress;
}

interface CommissionSummary {
  mtdCommissionPence: number;
  ytdCommissionPence: number;
  allTimeCommissionPence: number;
  txCount: number;
  annualGmvPence: number;
  plan?: Plan;
  tier: ServerTier;
  progress: ServerProgress;
}

interface ServerTransaction {
  id?: string;
  createdAt: number;
  grossPence: number;
  commissionPence?: number;
  status: string;
  description?: string;
}

interface TransactionsResponse {
  total: number;
  page: number;
  limit: number;
  items: ServerTransaction[];
}

// ── Normalised view-model used by render functions ──────────────────────────

interface InvoiceRow {
  /** First of the month for this invoice (display: "April 2026"). */
  monthStart: number;
  description: string;
  amountPence: number;
  paid: boolean;
}

interface OutstandingBalances {
  totalOutstandingPence: number;
  overduePence: number;
  activeProjects: number;
}

interface BillingView {
  /** Rolling 12-month platform turnover in pence. */
  rollingPence: number;
  /** Optional finer breakdown — null when backend can't supply it. */
  thisMonthPence: number | null;
  threeMonthRollingPence: number | null;
  /** Active commission plan (standard ladder unless admin-assigned enterprise). */
  plan: Plan;
  /** Resolved tier (current). */
  tier: Tier;
  /** Distance to next milestone in pence (0 if at top tier). */
  distancePence: number;
  /** Fractional progress within the current band, [0, 1]. */
  progressFraction: number;
  invoices: InvoiceRow[];
  outstanding: OutstandingBalances;
  /** True when any of the figures fell back to demo data. */
  isDemo: boolean;
}

// ── Formatters ───────────────────────────────────────────────────────────────

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const GBP_2DP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const MONTH_FMT = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" });

function gbpFromPence(pence: number): string {
  return GBP.format(Math.round(pence / 100));
}

function gbpFromPence2dp(pence: number): string {
  return GBP_2DP.format(pence / 100);
}

function shortGbp(gbp: number): string {
  if (gbp >= 1_000_000) return `£${(gbp / 1_000_000).toFixed(gbp % 1_000_000 === 0 ? 0 : 1).replace(/\.0$/, "")}m`;
  if (gbp >= 1000) return `£${Math.round(gbp / 1000)}k`;
  return GBP.format(gbp);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Tier helpers ─────────────────────────────────────────────────────────────

function tierForGbp(gbp: number, tiers: readonly Tier[] = TIERS): Tier {
  for (const t of tiers) {
    if (gbp >= t.minGbp && (t.maxGbp === null || gbp < t.maxGbp)) return t;
  }
  return tiers[tiers.length - 1]!;
}

function progressWithinBand(gbp: number, tier: Tier): { fraction: number; distancePence: number } {
  if (tier.maxGbp === null) return { fraction: 1, distancePence: 0 };
  const span = tier.maxGbp - tier.minGbp;
  const into = Math.max(0, gbp - tier.minGbp);
  const fraction = Math.max(0, Math.min(1, into / span));
  const distancePence = Math.max(0, Math.round((tier.maxGbp - gbp) * 100));
  return { fraction, distancePence };
}

// ── Demo fallback ────────────────────────────────────────────────────────────

function demoView(): BillingView {
  // Empty/zero state until live commission/Stripe figures load — no demo
  // turnover or invoices. The commission-tier maths still runs off the (zero)
  // turnover so the plan band renders correctly.
  const rollingPence = 0;
  const thisMonthPence = 0;
  const threeMonthRollingPence = 0;
  const tier = tierForGbp(rollingPence / 100);
  const { fraction, distancePence } = progressWithinBand(rollingPence / 100, tier);

  const invoices: InvoiceRow[] = [];

  return {
    rollingPence,
    thisMonthPence,
    threeMonthRollingPence,
    plan: "standard",
    tier,
    distancePence,
    progressFraction: fraction,
    invoices,
    outstanding: {
      totalOutstandingPence: 0,
      overduePence: 0,
      activeProjects: 0,
    },
    isDemo: true,
  };
}

// ── Backend fetch ────────────────────────────────────────────────────────────

/**
 * Loads the billing view from the backend, fanning out to the available
 * /api/stripe/* endpoints. If anything 404/501s, falls back to demo data
 * so the page still renders something sensible.
 */
async function loadView(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<BillingView> {
  // Fan out — the two we really need are connect/status and commission-summary.
  // transactions is best-effort (used to derive Recent Invoices); if absent
  // we fall back to the demo invoice rows.
  const [statusRes, summaryRes, txRes] = await Promise.allSettled([
    ctx.api<ConnectStatus>("/stripe/connect/status", { signal }),
    ctx.api<CommissionSummary>("/stripe/commission-summary", { signal }),
    ctx.api<TransactionsResponse>("/stripe/transactions?limit=24", { signal }),
  ]);

  if (signal.aborted) {
    throw new DOMException("aborted", "AbortError");
  }

  // If both core calls failed with non-404/501 errors, surface them.
  const statusOk = statusRes.status === "fulfilled";
  const summaryOk = summaryRes.status === "fulfilled";

  // 404/501 anywhere → graceful demo fallback.
  const isMissing = (r: PromiseSettledResult<unknown>): boolean =>
    r.status === "rejected" &&
    r.reason instanceof ApiError &&
    (r.reason.status === 404 || r.reason.status === 501);

  if (!statusOk && !summaryOk) {
    if (isMissing(statusRes) && isMissing(summaryRes)) {
      return demoView();
    }
    // Non-404 failure on both — let caller toast and render demo.
    const err = (statusRes as PromiseRejectedResult).reason;
    throw err;
  }

  const status = statusOk ? statusRes.value : null;
  const summary = summaryOk ? summaryRes.value : null;
  const transactions = txRes.status === "fulfilled" ? txRes.value.items : [];

  // Roll up rolling-turnover figure from whichever endpoint replied.
  const rollingPence =
    summary?.annualGmvPence ?? status?.annualGmvPence ?? 0;

  // Plan drives which ladder we render (standard unless admin-assigned enterprise).
  const plan: Plan = summary?.plan ?? status?.plan ?? "standard";
  const activeTiers = tiersForPlan(plan);

  const tier = tierForGbp(rollingPence / 100, activeTiers);
  const { fraction, distancePence } = progressWithinBand(rollingPence / 100, tier);

  // Build invoices from succeeded transactions grouped by month. Each
  // transaction's `commissionPence` is what the retailer was charged — sum
  // by month-start for a "platform commission" line.
  const byMonth = new Map<number, { amount: number; count: number }>();
  for (const t of transactions) {
    if (t.status !== "succeeded") continue;
    const d = new Date(t.createdAt);
    const key = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    const slot = byMonth.get(key) ?? { amount: 0, count: 0 };
    slot.amount += t.commissionPence ?? 0;
    slot.count += 1;
    byMonth.set(key, slot);
  }
  const invoices: InvoiceRow[] = [...byMonth.entries()]
    .filter(([, v]) => v.amount > 0)
    .map(([monthStart, v]) => ({
      monthStart,
      description: `Platform commission · ${v.count} transaction${v.count === 1 ? "" : "s"}`,
      amountPence: v.amount,
      paid: true,
    }))
    .sort((a, b) => b.monthStart - a.monthStart)
    .slice(0, 6);

  // This-month and 3-month-rolling are derivable when summary is present.
  const thisMonthPence = summary ? summary.mtdCommissionPence : null;
  const threeMonthRollingPence = (() => {
    if (transactions.length === 0) return null;
    const cutoff = Date.now() - 92 * 24 * 60 * 60 * 1000;
    return transactions
      .filter((t) => t.status === "succeeded" && t.createdAt >= cutoff)
      .reduce((sum, t) => sum + (t.grossPence || 0), 0);
  })();

  // Outstanding balances: there's no retailer-facing endpoint for unpaid
  // platform invoices yet, so render an empty/zero state rather than demo
  // figures. Stripe auto-collects, so for a Connected account these are zero.
  const outstanding: OutstandingBalances = { totalOutstandingPence: 0, overduePence: 0, activeProjects: 0 };

  // No live transactions → empty invoice list (no demo rows).
  const finalInvoices = invoices.length > 0 ? invoices : demoView().invoices;
  const isDemo = invoices.length === 0;

  // Honour the server's tier + progress when present (more accurate than
  // re-deriving locally — backend is the single source of truth).
  const serverTier = summary?.tier ?? status?.tier;
  const resolvedTier: Tier = serverTier
    ? activeTiers.find((t) => t.n === (serverTier.tier as Tier["n"])) ?? tier
    : tier;
  const serverProgress = summary?.progress ?? status?.progress;
  const resolvedFraction = serverProgress?.progressFraction ?? fraction;
  const resolvedDistancePence =
    serverProgress?.distancePence ??
    (serverProgress?.distanceGbp
      ? Math.round(parseFloat(serverProgress.distanceGbp) * 100)
      : distancePence);

  return {
    rollingPence,
    thisMonthPence,
    threeMonthRollingPence,
    plan,
    tier: resolvedTier,
    distancePence: resolvedDistancePence,
    progressFraction: resolvedFraction,
    invoices: finalInvoices,
    outstanding,
    isDemo,
  };
}

// ── Rendering ────────────────────────────────────────────────────────────────

function renderShell(container: HTMLElement): {
  rootEl: HTMLElement;
  badgeEl: HTMLElement;
  bannerEl: HTMLElement;
  tierEl: HTMLElement;
  loyaltyEl: HTMLElement;
  invoicesEl: HTMLElement;
  outstandingEl: HTMLElement;
} {
  container.innerHTML = `
    <section class="pay-root" aria-label="Payments and billing" data-perm="analytics">
      <header class="pay-header">
        <div class="pay-header__titles">
          <div class="pay-header__icon" aria-hidden="true">
            ${iconMarkup("card", { size: "md" })}
          </div>
          <div>
            <h1 class="pay-title">Payments &amp; Billing</h1>
            <p class="pay-subtitle">Your platform plan, loyalty progress and recent invoices</p>
          </div>
        </div>
        <span class="pay-header__badge" data-role="demo-badge" hidden>Demo data · live billing connects later</span>
      </header>

      <div class="pay-banner" data-role="banner" hidden></div>

      <div class="pay-grid">
        <div class="pay-col pay-col--main" data-role="tier"></div>
        <div class="pay-col pay-col--side">
          <div data-role="loyalty"></div>
          <div data-role="invoices"></div>
          <div data-role="outstanding"></div>
        </div>
      </div>
    </section>
  `;

  return {
    rootEl: container.querySelector<HTMLElement>(".pay-root")!,
    badgeEl: container.querySelector<HTMLElement>("[data-role='demo-badge']")!,
    bannerEl: container.querySelector<HTMLElement>("[data-role='banner']")!,
    tierEl: container.querySelector<HTMLElement>("[data-role='tier']")!,
    loyaltyEl: container.querySelector<HTMLElement>("[data-role='loyalty']")!,
    invoicesEl: container.querySelector<HTMLElement>("[data-role='invoices']")!,
    outstandingEl: container.querySelector<HTMLElement>("[data-role='outstanding']")!,
  };
}

function renderTierCard(host: HTMLElement, view: BillingView): void {
  const t = view.tier;
  const atMax = t.maxGbp === null;
  const pct = view.progressFraction * 100;
  const activeTiers = tiersForPlan(view.plan);
  const isEnterprise = view.plan === "enterprise";
  const planLabel = isEnterprise ? "ENTERPRISE PLAN" : "LOYALTY PLAN";

  const pips = activeTiers.map((tt) => {
    const cls = tt.n < t.n ? "done" : tt.n === t.n ? "current" : "";
    return `<div class="pay-tier-pip ${cls}" title="${escapeHtml(tt.label)} · ${tt.rate.toFixed(1)}%"></div>`;
  }).join("");

  const labels = activeTiers.map((tt) => {
    const cls = tt.n === t.n ? "is-current" : "";
    return `<span class="${cls}">${escapeHtml(tt.label)}</span>`;
  }).join("");

  const rollingBreakdown = `
    <dl class="pay-rolling-breakdown">
      <div>
        <dt>This month</dt>
        <dd>${view.thisMonthPence === null ? "—" : gbpFromPence(view.thisMonthPence)}</dd>
      </div>
      <div>
        <dt>3-month rolling</dt>
        <dd>${view.threeMonthRollingPence === null ? "—" : gbpFromPence(view.threeMonthRollingPence)}</dd>
      </div>
      <div>
        <dt>12-month rolling</dt>
        <dd>${gbpFromPence(view.rollingPence)}</dd>
      </div>
    </dl>
  `;

  const progressBlock = atMax
    ? `
      <div class="pay-tier-max">
        ${iconMarkup("star", { size: "md" })}
        <div>
          <div class="pay-tier-max__title">Maximum loyalty tier reached</div>
          <div class="pay-tier-max__sub">You're on the lowest commission rate we offer. Thank you for growing with us.</div>
        </div>
      </div>
    `
    : `
      <div class="pay-progress">
        <div class="pay-progress__meta">
          <span><b>${gbpFromPence(view.rollingPence)}</b> of <b>${t.maxGbp !== null ? GBP.format(t.maxGbp) : "—"}</b><span class="pay-progress__pct">${pct.toFixed(1)}%</span></span>
          <span>${gbpFromPence(view.distancePence)} to next milestone</span>
        </div>
        <div class="pay-bar"><div class="pay-bar__fill" style="width:${pct.toFixed(2)}%"></div></div>
      </div>
      <p class="pay-tier-hint">
        ${iconMarkup("info", { size: "sm" })}
        Your commission rate drops as your platform turnover grows. Next milestone unlocks at <b>${t.maxGbp !== null ? shortGbp(t.maxGbp) : "—"}</b> rolling turnover — keep going.
      </p>
    `;

  host.innerHTML = `
    <section class="pay-card pay-tier-card" aria-labelledby="pay-tier-title">
      <header class="pay-tier-card__head">
        <div class="pay-tier-card__title" id="pay-tier-title">
          ${iconMarkup("chart", { size: "sm" })}
          Tier Progress
        </div>
        <span class="pay-tier-card__pill">${escapeHtml(t.label.toUpperCase())} · ${planLabel}</span>
      </header>

      <div class="pay-tier-card__figure-label">Rolling 12-month platform turnover</div>
      <div class="pay-tier-card__figure">${gbpFromPence(view.rollingPence)}</div>

      ${rollingBreakdown}

      <div class="pay-tier-card__rate-row">
        <div class="pay-tier-card__rate">
          <div class="pay-tier-card__rate-label">Current commission rate</div>
          <div class="pay-tier-card__rate-val">
            <span class="pay-tier-card__rate-pct">${t.rate.toFixed(1)}%</span>
            <span class="pay-tier-card__rate-suffix">on platform turnover</span>
          </div>
        </div>
      </div>

      ${progressBlock}

      <div class="pay-tier-card__pips" aria-hidden="true">${pips}</div>
      <div class="pay-tier-card__pip-labels" aria-hidden="true">${labels}</div>
    </section>
  `;
}

function renderLoyaltyPanel(host: HTMLElement): void {
  host.innerHTML = `
    <section class="pay-card pay-side" aria-labelledby="pay-loyalty-title">
      <header class="pay-side__head">
        ${iconMarkup("info", { size: "sm" })}
        <h2 id="pay-loyalty-title">How loyalty pricing works</h2>
      </header>
      <ul class="pay-side__bullets">
        <li><b>Rolling 12-month turnover.</b> Your rate is calculated from your platform turnover over the trailing 12 months.</li>
        <li><b>Automatic rate reduction.</b> Cross a tier threshold and the lower rate applies — no negotiation, no forms.</li>
        <li><b>No contracts.</b> Zero monthly fee. You only pay commission on transactions processed through TKOS.</li>
        <li><b>Monthly billing review.</b> Turnover is reviewed at the start of every billing month; the lower rate applies to that month's invoice.</li>
      </ul>
    </section>
  `;
}

function renderInvoicesPanel(host: HTMLElement, view: BillingView): void {
  if (view.invoices.length === 0) {
    host.innerHTML = `
      <section class="pay-card pay-side" aria-labelledby="pay-invoices-title">
        <header class="pay-side__head">
          ${iconMarkup("file", { size: "sm" })}
          <h2 id="pay-invoices-title">Recent platform invoices</h2>
        </header>
        <p class="pay-side__empty">No invoices yet. Your monthly platform commission invoices will appear here.</p>
      </section>
    `;
    return;
  }

  const rows = view.invoices
    .map((inv) => {
      const month = MONTH_FMT.format(new Date(inv.monthStart));
      const amount = gbpFromPence2dp(inv.amountPence);
      const paidPill = inv.paid ? `<span class="pay-pill pay-pill--paid">PAID</span>` : "";
      return `
        <li class="pay-invoice">
          <div class="pay-invoice__main">
            <div class="pay-invoice__month">${escapeHtml(month)}</div>
            <div class="pay-invoice__desc">${escapeHtml(inv.description)}</div>
          </div>
          <div class="pay-invoice__right">
            <div class="pay-invoice__amount">${escapeHtml(amount)}</div>
            ${paidPill}
          </div>
        </li>
      `;
    })
    .join("");

  host.innerHTML = `
    <section class="pay-card pay-side" aria-labelledby="pay-invoices-title">
      <header class="pay-side__head">
        ${iconMarkup("file", { size: "sm" })}
        <h2 id="pay-invoices-title">Recent platform invoices</h2>
      </header>
      <ul class="pay-invoice-list">${rows}</ul>
    </section>
  `;
}

function renderOutstanding(host: HTMLElement, view: BillingView): void {
  const o = view.outstanding;
  host.innerHTML = `
    <section class="pay-card pay-outstanding" aria-labelledby="pay-outstanding-title">
      <header class="pay-side__head">
        ${iconMarkup("alert-triangle", { size: "sm" })}
        <h2 id="pay-outstanding-title">Outstanding balances</h2>
      </header>
      <div class="pay-outstanding__grid">
        <div class="pay-outstanding__cell">
          <div class="pay-outstanding__label">Total outstanding</div>
          <div class="pay-outstanding__value">${gbpFromPence(o.totalOutstandingPence)}</div>
        </div>
        <div class="pay-outstanding__cell pay-outstanding__cell--danger">
          <div class="pay-outstanding__label">Overdue</div>
          <div class="pay-outstanding__value">${gbpFromPence(o.overduePence)}</div>
        </div>
        <div class="pay-outstanding__cell">
          <div class="pay-outstanding__label">Active projects</div>
          <div class="pay-outstanding__value">${o.activeProjects}</div>
        </div>
      </div>
    </section>
  `;
}

// ── Page ─────────────────────────────────────────────────────────────────────

const paymentsPage: Page = {
  name: "payments",
  label: "Payments & Billing",
  icon: "",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const { badgeEl, bannerEl, tierEl, loyaltyEl, invoicesEl, outstandingEl } =
      renderShell(container);

    let controller: AbortController | null = null;
    let disposed = false;

    const setBanner = (msg: string | null, kind: "info" | "error" = "info"): void => {
      if (!msg) {
        bannerEl.hidden = true;
        bannerEl.textContent = "";
        bannerEl.classList.remove("pay-banner--error");
        return;
      }
      bannerEl.hidden = false;
      bannerEl.textContent = msg;
      bannerEl.classList.toggle("pay-banner--error", kind === "error");
    };

    const setBadge = (visible: boolean): void => {
      badgeEl.hidden = !visible;
    };

    const renderAll = (view: BillingView): void => {
      renderTierCard(tierEl, view);
      renderLoyaltyPanel(loyaltyEl);
      renderInvoicesPanel(invoicesEl, view);
      renderOutstanding(outstandingEl, view);
      setBadge(view.isDemo);
    };

    const load = async (): Promise<void> => {
      if (disposed) return;
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;

      try {
        const view = await loadView(ctx, signal);
        if (disposed || signal.aborted) return;
        // Onboarding gate: never frame a real (not-yet-onboarded) retailer
        // account's empty billing state as demo. Figures are already zeroed;
        // drop `isDemo` so the "Demo data" badge + toast don't appear.
        if (!demoDataAllowed(ctx)) view.isDemo = false;
        renderAll(view);
        if (view.isDemo) {
          setBanner(
            "Live billing data not yet available — showing demo figures. Connect Stripe to see real invoices.",
            "info",
          );
        } else {
          setBanner(null);
        }
      } catch (err) {
        if (signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't load billing (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Couldn't load billing";
        // Fall back to the empty/zero view so the page still renders. For a
        // real (not-yet-onboarded) account, suppress the demo framing.
        const fallback = demoView();
        if (!demoDataAllowed(ctx)) fallback.isDemo = false;
        renderAll(fallback);
        setBanner(`${msg}.`, "error");
        ctx.showToast(msg, "error");
      }
    };

    void load();

    return (): void => {
      disposed = true;
      controller?.abort();
      controller = null;
    };
  },
};

registerPage(paymentsPage);

export default paymentsPage;
